import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoleType } from 'utils';
import { LoadingScreen } from '../components/LoadingScreen';
import useEvolveUser from '../hooks/useEvolveUser';

/**
 * AuthCallbackPage - Página que maneja el callback de Auth0 después del login
 *
 * IMPORTANTE: NO llamamos manualmente a handleRedirectCallback().
 * El Auth0Provider maneja el callback automáticamente cuando se configura con onRedirectCallback.
 * Esta página solo espera a que Auth0 termine de cargar y luego redirige al usuario.
 *
 * VALIDACIÓN DE ROLES: Verifica que el usuario tenga un rol de profesional válido para acceder al portal EHR.
 */
export default function AuthCallbackPage(): JSX.Element {
  const { isLoading, error, isAuthenticated, logout } = useAuth0();
  const navigate = useNavigate();
  const currentUser = useEvolveUser();
  const [roleValidationError, setRoleValidationError] = useState<string | null>(null);

  useEffect(() => {
    // Solo redirigir cuando Auth0 haya terminado de cargar
    if (!isLoading && isAuthenticated) {
      // Esperar a que el usuario esté cargado antes de validar roles
      if (currentUser) {
        // Validar que el usuario tenga un rol de profesional válido
        const hasValidRole = currentUser.hasRole([
          RoleType.Administrator,
          RoleType.Staff,
          RoleType.Manager,
          RoleType.Provider,
          RoleType.CustomerSupport,
        ]);

        if (!hasValidRole) {
          console.warn('Usuario sin rol de profesional válido intentando acceder al portal EHR');
          setRoleValidationError(
            'No tienes permisos para acceder a este portal. Este portal es exclusivo para profesionales.'
          );

          // Cerrar sesión después de 3 segundos
          setTimeout(() => {
            void logout({
              logoutParams: {
                returnTo: import.meta.env.VITE_APP_OYSTEHR_APPLICATION_REDIRECT_URL,
                federated: true,
              },
            });
          }, 3000);
          return;
        }

        // Usuario válido: redirigir a la página correspondiente
        const redirectDestination = localStorage.getItem('redirectDestination');

        if (redirectDestination) {
          console.log('Redirigiendo a ruta guardada:', redirectDestination);
          localStorage.removeItem('redirectDestination');
          navigate(redirectDestination, { replace: true });
        } else {
          // Si está autenticado pero no hay destino guardado
          // Verificar roles para determinar la página de inicio
          const isProviderOrStaff = currentUser.hasRole([RoleType.Provider, RoleType.Staff, RoleType.CustomerSupport]);

          if (isProviderOrStaff) {
            navigate('/home', { replace: true });
          } else {
            navigate('/home', { replace: true });
          }
        }
      }
    } else if (!isLoading && !isAuthenticated) {
      // Si no está autenticado, ir a home
      console.log('Usuario no autenticado, redirigiendo a home');
      navigate('/', { replace: true });
    }
  }, [isLoading, isAuthenticated, currentUser, navigate, logout]);

  // Mostrar error de validación de roles
  if (roleValidationError) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
          px: 3,
        }}
      >
        <Typography variant="h5" color="error" textAlign="center">
          Acceso Denegado
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          {roleValidationError}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Cerrando sesión...
        </Typography>
      </Box>
    );
  }

  // Si hay un error, mostrar mensaje y redirigir
  if (error) {
    console.error('Error de Auth0:', error);

    // Redirigir después de un momento
    setTimeout(() => {
      navigate('/', { replace: true });
    }, 2000);

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <Typography variant="h5" color="error">
          Error de Autenticación
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {error.message || 'Ocurrió un error durante la autenticación'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Redirigiendo...
        </Typography>
      </Box>
    );
  }

  // Mostrar pantalla de carga mientras Auth0 está cargando
  return <LoadingScreen />;
}
